import { useId, type ReactNode } from 'react'

type Props = {
  checked: boolean
  onCheckedChange: (next: boolean) => void
  label: ReactNode
  /** When set, label is shown as a tool-style title with this description below (sandbox uses label-only, uppercase). */
  description?: ReactNode
  /** Tighter row for dense toolbars (e.g. sandbox preview panel). */
  compact?: boolean
  /** Native tooltip on the row (labels are short). */
  title?: string
}

export default function PanelSwitch({
  checked,
  onCheckedChange,
  label,
  description,
  compact,
  title,
}: Props) {
  const labelId = useId()
  const descId = useId()
  const hasDesc = description != null

  return (
    <div
      title={title}
      className={`flex items-start justify-between rounded border border-outline-variant/10 bg-surface-container-lowest ${
        compact ? 'gap-2 p-1.5' : 'gap-3 p-2'
      }`}
    >
      <div className="min-w-0 flex-1">
        {hasDesc ? (
          <div className={`flex flex-col ${compact ? 'gap-0.5' : 'gap-1'}`}>
            <span
              id={labelId}
              className={`font-mono font-semibold text-on-surface ${compact ? 'text-[9px]' : 'text-[10px]'}`}
            >
              {label}
            </span>
            <span
              id={descId}
              className={`font-mono leading-snug text-on-surface-variant/90 ${compact ? 'text-[9px]' : 'text-[10px]'}`}
            >
              {description}
            </span>
          </div>
        ) : (
          <span
            id={labelId}
            className={`font-label tracking-tighter text-on-surface uppercase ${compact ? 'text-[10px] leading-tight' : 'text-[11px]'}`}
          >
            {label}
          </span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        {...(hasDesc ? { 'aria-describedby': descId } : {})}
        onClick={() => onCheckedChange(!checked)}
        className={`relative h-4 w-8 shrink-0 cursor-pointer self-center rounded-full p-0.5 transition-colors ${
          checked ? 'bg-primary-container' : 'bg-surface-container-highest'
        }`}
      >
        <span
          className={`block size-3 rounded-full transition-transform ${
            checked ? 'translate-x-4 bg-tertiary-fixed' : 'translate-x-0 bg-on-surface/40'
          }`}
        />
      </button>
    </div>
  )
}
